import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Phone, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Primitives";
import { SERVICES, IMAGES, BUSINESS, SERVICE_PRICING } from "@/data/site";
import { trackEvent } from "@/lib/api";

const IMG_MAP = {
  babysitting: IMAGES.sitterReading,
  "hotel-care": IMAGES.familyMoment,
  "church-care": IMAGES.childPlayful,
  "special-events": IMAGES.team,
  "overnight-care": IMAGES.bedtime,
  "pop-in-pa": IMAGES.familyMoment,
};

export default function ServiceDetail() {
  const { serviceId } = useParams();
  const service = SERVICES.find((s) => s.id === serviceId);
  if (!service) return <Navigate to="/services" replace />;

  return (
    <>
      <Seo
        title={service.name}
        description={`${service.name} in DFW — ${service.short} 11-step screened, CPR-certified caregivers from Mom's Best Friend Sitters.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: service.name,
          description: service.short,
          provider: { "@type": "Organization", name: BUSINESS.name },
          areaServed: "Dallas–Fort Worth, TX",
        }}
        breadcrumbs={[{ name: "Home", item: "/" }, { name: "Services", item: "/services" }, { name: service.name, item: `/services/${service.id}` }]}
      />

      <section className="bg-parchment-2 border-b border-[#F1E6D0]">
        <div className="container-mbf py-14 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Link to="/services" className="flex w-fit items-center gap-1.5 text-sm text-terracotta font-semibold mb-6 hover:gap-2.5 transition-all" data-testid="service-back-link">
              <ArrowLeft className="w-4 h-4" /> All services
            </Link>
            <p className="overline mb-3">{service.tagline}</p>
            <h1 className="text-4xl md:text-5xl font-serif tracking-tight leading-[1.1] text-espresso">{service.name}</h1>
            <p className="mt-5 text-base md:text-lg text-stoneish leading-relaxed max-w-xl">{service.outcome}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/book" onClick={() => trackEvent("cta_click", { source: `service_${service.id}` })} className="btn-accent !px-8 !py-4" data-testid="service-request-cta">
                Request this service <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={BUSINESS.phoneHref} className="btn-outline !px-8 !py-4" data-testid="service-phone-link"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
            </div>
          </div>
          <div className="rounded-[2rem] overflow-hidden shadow-warm border border-[#F1E6D0]">
            <img src={IMG_MAP[service.id]} alt={service.name} className="w-full h-[340px] md:h-[440px] object-cover" />
          </div>
        </div>
      </section>

      <section className="container-mbf py-20 md:py-28 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <p className="overline mb-3">What's included</p>
          <h2 className="font-serif text-2xl md:text-3xl text-espresso mb-6">Everything your family needs</h2>
          <ul className="space-y-4">
            {service.features.map((f) => (
              <li key={f} className="flex items-start gap-3 text-base text-espresso">
                <span className="w-6 h-6 rounded-full bg-sage/15 text-sage flex items-center justify-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                {f}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="bg-parchment-2 rounded-2xl border border-[#F1E6D0] p-8">
            <h3 className="font-serif text-xl text-espresso mb-4">Good to know</h3>
            <ul className="space-y-4 text-sm text-stoneish">
              <li className="flex justify-between border-b border-[#F1E6D0] pb-3"><span>Minimum booking</span><span className="font-semibold text-espresso">{BUSINESS.minHours} hours</span></li>
              <li className="flex justify-between border-b border-[#F1E6D0] pb-3"><span>Sitter pay</span><span className="font-semibold text-espresso">$20/hr</span></li>
              <li className="flex justify-between border-b border-[#F1E6D0] pb-3"><span>Additional child</span><span className="font-semibold text-espresso">+$1/hr</span></li>
              <li className="flex justify-between"><span>Screening</span><span className="font-semibold text-espresso">11-step verified</span></li>
            </ul>
            <p className="text-xs text-stoneish mt-4 bg-tinted rounded-xl p-3 leading-relaxed" data-testid="service-price-note">{SERVICE_PRICING[service.id]}</p>
            <Link to="/book" className="btn-primary w-full mt-6" data-testid="service-detail-book-cta">Start a request <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </Reveal>
      </section>

      <section className="container-mbf pb-24">
        <div className="rounded-[2rem] bg-espresso text-parchment p-10 md:p-14 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-white">Other services you might need</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {SERVICES.filter((s) => s.id !== service.id).map((s) => (
              <Link key={s.id} to={`/services/${s.id}`} className="bg-parchment/10 hover:bg-terracotta border border-parchment/15 hover:border-terracotta px-5 py-2.5 rounded-full text-sm font-medium transition-colors" data-testid={`service-related-${s.id}`}>
                {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
